import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../utils/api';
import { useVoice } from '../context/VoiceContext';
import { useSettings } from '../context/SettingsContext';
import { playAlertBeep } from '../utils/alertSound';
import { hasVibrate } from '../utils/deviceCapability';

/**
 * 「提醒方式」→ 该动用哪些提醒手段。
 *
 * 原来这里是三个独立布尔（提示铃声 / 提醒振动 / 到点语音播报），
 * 用户要在三处分别判断谁管谁，而且「关掉语音但留着铃声」这种组合
 * 实际很难靠三个开关讲清楚。现在收敛成一个枚举，一处选完。
 */
function resolveChannels(mode) {
  const m = mode || 'all';
  return {
    voice: m === 'all' || m === 'voice',
    sound: m === 'all' || m === 'sound',
    vibrate: m === 'all' || m === 'vibrate',
  };
}

export function useReminderPoll(enabled = true) {
  const [activeReminder, setActiveReminder] = useState(null);
  const [todayReminders, setTodayReminders] = useState([]);
  const [snoozeCount, setSnoozeCount] = useState(0);
  const { speak } = useVoice();
  const { settings } = useSettings();
  const intervalRef = useRef(null);
  const announceCountRef = useRef(0);
  const lastAnnounceRef = useRef(0);
  const activeIdRef = useRef(null);

  /**
   * 弹出一次提醒：按当前「提醒方式」决定念不念、响不响、振不振。
   *
   * 振动在 iPhone 上一定不会发生 —— Safari 从未实现 Vibration API。
   * 这里保留能力检测，避免在没有该接口的浏览器上直接抛错中断后续流程。
   */
  const fireAlert = useCallback((reminder, isRepeat) => {
    const medName = reminder.medicine_name || '药品';
    const dosage = reminder.dosage || '';
    const unit = reminder.unit || '片';
    const ch = resolveChannels(settings.reminder_mode);

    if (ch.voice) {
      // 到点提醒是这个产品唯一会"主动开口"的地方，语气走「关怀」口径（设计文档 §7.3）：
      // 说的是"叮嘱"而不是"通知"。但关怀不等于含糊 —— 药名、剂量一个不少。
      if (isRepeat) {
        speak(`别忘了吃药哦，${medName}，${dosage} ${unit}。`, { type: 'announce' });
      } else {
        const userName = settings.user_name || '';
        speak(`${userName}，该吃药啦。记得服用 ${medName}，${dosage} ${unit} 哦。`, { type: 'announce' });
      }
    }
    // 首次提醒连响两声，重复提醒响一声，避免久等时太吵
    if (ch.sound) playAlertBeep(isRepeat ? 1 : 2);
    if (ch.vibrate && hasVibrate()) {
      try {
        navigator.vibrate(isRepeat ? [200, 100, 200] : [400, 150, 400]);
      } catch (e) {
        // 振动失败不影响提醒本身
      }
    }
  }, [speak, settings.user_name, settings.reminder_mode]);

  const checkReminders = useCallback(async () => {
    if (!enabled) return;
    try {
      const result = await api.checkReminders();
      const triggered = result && result.reminders ? result.reminders : [];
      if (triggered.length > 0) {
        const reminder = triggered[0];
        if (activeIdRef.current !== reminder.id) {
          // 新提醒：弹窗 + 按提醒方式发声
          activeIdRef.current = reminder.id;
          announceCountRef.current = 0;
          lastAnnounceRef.current = 0;
          setActiveReminder(reminder);
          // 沿用服务端记录的稍后提醒次数，刷新页面后不会被重置
          setSnoozeCount(reminder.snooze_count || 0);
          fireAlert(reminder, false);
          // 回写「已送达设备」：服务端据此知道提醒真的弹出来了，也避免同一条反复弹。
          // 这个回执是提醒日志里区分「服务端起过」和「设备收到过」的关键，失败不阻断提醒。
          api.markReminderDelivered(reminder.id).catch(() => {});
        } else {
          // 同一提醒：每5分钟重新提醒，最多3次（受重复提醒设置控制）
          const now = Date.now();
          if (settings.reminder_repeat && announceCountRef.current < 3 && now - lastAnnounceRef.current >= 5 * 60 * 1000) {
            announceCountRef.current += 1;
            lastAnnounceRef.current = now;
            fireAlert(reminder, true);
          }
        }
      }
      const today = await api.getTodayReminders();
      setTodayReminders(today);
    } catch (err) {
      // 静默处理轮询错误
    }
  }, [fireAlert, enabled, settings.reminder_repeat]);

  useEffect(() => {
    if (!enabled) return;
    checkReminders();
    intervalRef.current = setInterval(checkReminders, 30000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [checkReminders, enabled]);

  const confirmReminder = useCallback(async (status) => {
    if (!activeReminder) return;
    try {
      await api.confirmReminder(activeReminder.id, { action: status });
      const medName = activeReminder.medicine_name || '药品';
      // 确认/跳过属于「用户点击后的操作反馈」，走界面操作播报通道。
      // 这两句是"回话"，所以用日常对话的说法：人对老人的一句「记下了，放心」，
      // 比「已确认服用 XX，1 片」这种回执更像在跟他说话。
      if (status === 'taken') {
        speak(`好的，${medName} 已记下，请放心。`);
      } else if (status === 'skip') {
        speak(`好的，这次先跳过。下次记得按时吃哦。`);
      }
      setActiveReminder(null);
      setSnoozeCount(0);
      activeIdRef.current = null;
      announceCountRef.current = 0;
      const today = await api.getTodayReminders();
      setTodayReminders(today);
    } catch (err) {
      // 错误处理
    }
  }, [activeReminder, speak]);

  /**
   * 稍后提醒：必须落到服务端。
   *
   * 原来的实现只把弹窗关掉、本地计数 +1，服务端完全不知道 ——
   * 老人点了「稍后提醒」，界面安静了，服务端那边照旧按原时间判漏服。
   * 现在由服务端把这条提醒的触发时间顺延 10 分钟并回传新次数。
   */
  const snoozeReminder = useCallback(async () => {
    if (!activeReminder) return;
    const medName = activeReminder.medicine_name || '药品';
    setActiveReminder(null);
    // 清掉本地 id 记录：顺延到点后要让它重新弹出来
    activeIdRef.current = null;
    announceCountRef.current = 0;
    try {
      const res = await api.confirmReminder(activeReminder.id, { action: 'snooze' });
      if (res && typeof res.snooze_count === 'number') setSnoozeCount(res.snooze_count);
      if (res && res.status === 'missed') {
        // 这一句里有「漏服」——它是**事实陈述**，按口径必须准（设计文档 §7.3：
        // 暖只用在鼓励和提醒，状态与事实照旧用术语）。后半句才是关怀的部分。
        speak(`${medName}这一次记作漏服了，下一顿记得按时吃哦。`, { type: 'announce' });
      } else if (res && res.next_trigger_at) {
        const hhmm = String(res.next_trigger_at).slice(11, 16);
        speak(`好的，${hhmm} 再提醒您吃 ${medName}。`);
      } else {
        speak('好的，过一会儿再提醒您。');
      }
    } catch (err) {
      speak('稍后提醒没设置成功，您可以先确认或跳过。');
    }
    try {
      const today = await api.getTodayReminders();
      setTodayReminders(today);
    } catch (err) {
      // 拉取失败不影响弹窗关闭
    }
  }, [activeReminder, speak]);

  return { activeReminder, todayReminders, snoozeCount, confirmReminder, snoozeReminder };
}
